import { z } from 'zod';
import { ApiScope, ApiScopeSchema, ApiTenantScopeSchema } from './publicApi';

/**
 * Webhooks de saída da API pública v1 — contrato compartilhado entre Functions
 * (emissão, fan-out, entrega, callable do portal), o portal de desenvolvedores
 * (`apps/developers`) e o gerador da documentação (`scripts/gen-portal-content.ts`).
 *
 * Origem: #792 — 2026-10-02 12:53 +0200 (commit `7204819a7`).
 *
 * Por quê: a API v1 (#263) era só leitura e o parceiro fazia polling. Agora o
 * parceiro assina, no portal, os eventos que quer receber. Três decisões do
 * owner (2026-10-02) dão a forma deste arquivo:
 *  - a assinatura é gerida SÓ pelo portal — não há REST `webhooks:manage`;
 *  - a assinatura HERDA o alcance de tenants da integração (`ApiKey.tenantScope`
 *    + `scopePartnerId` + `demoTenantIds`, #785), reavaliado a cada entrega;
 *  - o catálogo cobre todos os recursos do tenant que têm fato operacional.
 *
 * Contrato: tudo aqui é puro (sem I/O). O catálogo `WEBHOOK_EVENT_CATALOG` é a
 * fonte única de "quais eventos existem, a que grupo pertencem e qual scope de
 * leitura os autoriza" — UI, doc e fan-out derivam dele.
 *
 * Usado por: Functions `apps/functions/src/webhooks/**` e
 * `apps/functions/src/devPortal/webhooks.ts`; triggers que emitem
 * (`onLabelWritten`, `onProductWritten`, `onSupplierWritten`, `onPrinterWritten`,
 * `onMemberWritten`, `onLabelTemplateWritten`, `onTenantWritten`,
 * `onActivityLogCreated`, callables de `responsibles`, `lib/stockAlert.ts`,
 * `scanExpiringLabels`); portal `apps/developers/src/routes/Webhook*Page.tsx`;
 * gerador `scripts/gen-portal-content.ts` (seção `webhooks` do OpenAPI).
 *
 * Arquitetura: Firestore é a fonte da verdade da assinatura; o segredo de
 * assinatura NÃO é persistido (derivado de um secret mestre no servidor). O
 * envelope segue o Standard Webhooks (https://www.standardwebhooks.com/).
 *
 * Dicas: evento novo = entrada nova em `WebhookEventType` + `WEBHOOK_EVENT_CATALOG`
 * (o `Record` completo não compila sem ela) + emissão no trigger + doc. Nunca
 * renomear um tipo existente: o parceiro programa contra a string.
 *
 * Issues: #792 (origem), #263 (API v1), #785 (alcance por regra), #761
 * (constante por conceito).
 *
 * Histórico:
 *  - 2026-10-02 12:53 +0200 — (#792) criação.
 *  - 2026-10-02 13:10 +0200 — (#792) input/output da callable `devPortalWebhooks`.
 *  - 2026-10-02 14:19 +0200 — (#792) auditoria: ponto final no hostname,
 *    `ping_not_subscribable`, faixas de URL.
 *  - 2026-10-02 15:40 +0200 — (#792) `listEvents` (tela "Event log") e
 *    `history` por entrega.
 *  - 2026-10-02 19:17 +0200 — (#792) timeout de entrega 10 s → 2 s (owner,
 *    commit `8a2973ace`).
 *  - 2026-10-06 11:40 +0200 — (#795) timeout de entrega 2 s → 5 s (owner).
 */

// ---------------------------------------------------------------------------
// Limites e constantes
// ---------------------------------------------------------------------------

/** Assinaturas por integração. Folgado: cada uma já recebe N tipos de evento. */
export const MAX_WEBHOOK_SUBSCRIPTIONS_PER_KEY = 10;
/** Teto da URL — o que cabe com folga em qualquer servidor HTTP e no Firestore. */
export const WEBHOOK_URL_MAX_LENGTH = 2048;
export const WEBHOOK_DESCRIPTION_MAX_LENGTH = 120;
/**
 * Retenção de `outbound_webhook_events` e `outbound_webhook_deliveries` (TTL
 * `expireAt`). 30 dias = janela de "reenviar" do portal e de depuração.
 */
export const WEBHOOK_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;
/** Validade da resposta guardada por Idempotency-Key de ação do portal. */
export const WEBHOOK_IDEMPOTENCY_TTL_MS = 24 * 60 * 60 * 1000;
/**
 * Janela em que a versão anterior do segredo continua assinando depois de uma
 * rotação. Dá ao parceiro tempo de trocar o segredo sem perder evento.
 */
export const WEBHOOK_SECRET_ROTATION_GRACE_MS = 24 * 60 * 60 * 1000;
/**
 * Timeout por tentativa de entrega. O receptor deve só verificar a assinatura,
 * gravar o evento na fila dele e responder 2xx — trabalho de milissegundos.
 *
 * Decisão do owner 2026-10-02 19:14 +0200 (#792): 10 s → 2 s. Dez segundos
 * contradiziam o próprio contrato ("responda rápido, processe depois"): quem
 * tem fila não precisa de 10 s para gravar nela. Aumentar de novo legitima
 * receptor que processa dentro da requisição. A doc pública lê este valor
 * (`WEBHOOK_TIMEOUT_SECONDS` em `scripts/gen-portal-content.ts`) — regenerar
 * com `npm run portal:content` no mesmo commit.
 *
 * Decisão do owner 2026-10-06 11:40 +0200 (#795): 2 s → 5 s. Com 2 s, um
 * parceiro que já tinha gravado o evento e respondido 2xx um pouco depois do
 * prazo recebia a entrega de novo (duplicata), e o retry contava como falha
 * rumo ao desligamento. 5 s dá folga a esse 2xx tardio sem voltar aos 10 s
 * que legitimavam processar dentro da requisição (o motivo da #792).
 *
 * O prazo vale até o STATUS da resposta (#795, 2026-10-06 11:13 +0200): é o
 * que a doc pública promete ("return any 2xx within N seconds"). O corpo tem
 * teto próprio em `WEBHOOK_RESPONSE_BODY_GRACE_MS` (`webhooks/safeFetch.ts`) e
 * nunca transforma um 2xx em timeout.
 */
export const WEBHOOK_DELIVERY_TIMEOUT_MS = 5_000;
/**
 * Tentativas por entrega (Cloud Tasks `retryConfig.maxAttempts`, conta a
 * primeira). Backoff dobra de 30 s até o teto de 6 h: as 10 primeiras esperas
 * somam ~8,5 h e as 9 seguintes 6 h cada — ~2,6 dias até `EXHAUSTED`. A doc
 * pública promete "cerca de 2 dias e meio"; mudar aqui exige mudar lá.
 */
export const WEBHOOK_MAX_ATTEMPTS = 20;
/** Backoff do Cloud Tasks (segundos) — ver `WEBHOOK_MAX_ATTEMPTS`. */
export const WEBHOOK_MIN_BACKOFF_SECONDS = 30;
export const WEBHOOK_MAX_BACKOFF_SECONDS = 6 * 60 * 60;
/**
 * Rodadas do sweep (a cada 10 min) que podem reencontrar uma entrega ainda
 * `PENDING` antes de ela ser fechada como `EXHAUSTED`/`stuck_pending` — 18 ≈ 3 h.
 *
 * Origem: #795 — 2026-10-06 11:13 +0200 (auditoria geral: entrega "veneno"
 * travava a fila do sweep).
 *
 * Por quê 3 h: depois de uma tarefa descartada o Cloud Tasks reserva o nome por
 * ~1 h, e uma fila com backlog grande (20 POSTs simultâneos) pode atrasar o 1º
 * dispatch de uma emissão em massa; 3 h cobre os dois com folga sem deixar a
 * entrega presa até a TTL de 30 dias. Ver `webhooks/sweep.ts`.
 */
export const WEBHOOK_PENDING_MAX_REQUEUES = 18;
/**
 * Entregas esgotadas SEGUIDAS (sem nenhum sucesso no meio) que desligam a
 * assinatura (`DISABLED_FAILING`). Cada uma já é ~2,6 dias de tentativas, então
 * 5 seguidas é um endpoint morto, não um soluço.
 */
export const WEBHOOK_DISABLE_AFTER_CONSECUTIVE_EXHAUSTED = 5;
/** Quanto do corpo da resposta do parceiro guardamos, para depuração no portal. */
export const WEBHOOK_RESPONSE_EXCERPT_MAX = 2048;
/** Página do log de entregas no portal. */
export const WEBHOOK_DELIVERIES_PAGE_SIZE = 25;
/**
 * Tentativas guardadas no histórico de cada entrega (#792, log de auditoria do
 * portal). 20 = `WEBHOOK_MAX_ATTEMPTS`: uma cadeia inteira cabe; reenvios
 * manuais empurram as mais velhas para fora.
 */
export const WEBHOOK_DELIVERY_HISTORY_MAX = 20;

/** Versão do envelope. Muda só com `/v2` da API. */
export const WEBHOOK_API_VERSION = 'v1';

/**
 * Headers de toda entrega. Os três `webhook-*` são os do Standard Webhooks
 * (bibliotecas prontas em várias linguagens verificam). `Idempotency-Key` é o
 * id do evento — IGUAL em todas as tentativas e no reenvio manual — e é o que o
 * parceiro usa para deduplicar.
 */
export const WebhookHeader = {
  ID: 'webhook-id',
  TIMESTAMP: 'webhook-timestamp',
  SIGNATURE: 'webhook-signature',
  IDEMPOTENCY_KEY: 'Idempotency-Key',
  EVENT_TYPE: 'EtiquetaChef-Event-Type',
  USER_AGENT: 'User-Agent',
} as const;
export const WEBHOOK_USER_AGENT = 'EtiquetaChef-Webhooks/1.0';
/** Prefixo do segredo exibido ao parceiro (convenção Standard Webhooks). */
export const WEBHOOK_SECRET_PREFIX = 'whsec_';

/** Coleções raiz (Firestore). Nomes exclusivos por causa da TTL por collectionGroup (#620). */
export const WebhookCollection = {
  SUBSCRIPTIONS: 'outbound_webhook_subscriptions',
  EVENTS: 'outbound_webhook_events',
  DELIVERIES: 'outbound_webhook_deliveries',
  IDEMPOTENCY: 'outbound_webhook_idempotency',
} as const;

// ---------------------------------------------------------------------------
// Catálogo de eventos
// ---------------------------------------------------------------------------

/**
 * Tipos de evento. Formato `<recurso>.<fato no passado>`, em inglês, porque é o
 * que o parceiro lê no payload e na doc (a API inteira é em inglês).
 *
 * Fora de propósito: etiqueta em rascunho (`DRAFT`) não gera evento — a API v1
 * declara que rascunho não é fato operacional (`PUBLIC_LABEL_STATUSES`). O
 * primeiro evento de uma etiqueta é `label.printed`.
 */
export const WebhookEventType = {
  // Etiquetas — ciclo de vida
  LABEL_PRINTED: 'label.printed',
  LABEL_CONSUMED: 'label.consumed',
  LABEL_DISCARDED: 'label.discarded',
  LABEL_EXPIRED: 'label.expired',
  LABEL_MIXED: 'label.mixed',
  LABEL_DELETED: 'label.deleted',
  LABEL_RESTORED: 'label.restored',
  LABEL_UNDONE: 'label.undone',
  LABEL_MOVEMENT_RECORDED: 'label.movement_recorded',
  LABEL_OVERRUN: 'label.overrun',
  // Etiquetas — alertas e impressão
  LABEL_EXPIRING_SOON: 'label.expiring_soon',
  LABEL_REPRINTED: 'label.reprinted',
  LABEL_PRINT_FAILED: 'label.print_failed',
  // Produtos
  PRODUCT_CREATED: 'product.created',
  PRODUCT_UPDATED: 'product.updated',
  PRODUCT_ACTIVATED: 'product.activated',
  PRODUCT_DEACTIVATED: 'product.deactivated',
  PRODUCT_DELETED: 'product.deleted',
  PRODUCT_STOCK_LOW: 'product.stock_low',
  PRODUCT_STOCK_EMPTY: 'product.stock_empty',
  PRODUCT_STOCK_RESTORED: 'product.stock_restored',
  // Fornecedores
  SUPPLIER_CREATED: 'supplier.created',
  SUPPLIER_UPDATED: 'supplier.updated',
  SUPPLIER_ACTIVATED: 'supplier.activated',
  SUPPLIER_DEACTIVATED: 'supplier.deactivated',
  // Impressoras
  PRINTER_CREATED: 'printer.created',
  PRINTER_UPDATED: 'printer.updated',
  PRINTER_DELETED: 'printer.deleted',
  // Modelos de etiqueta
  LABEL_TEMPLATE_CREATED: 'label_template.created',
  LABEL_TEMPLATE_UPDATED: 'label_template.updated',
  LABEL_TEMPLATE_DELETED: 'label_template.deleted',
  // Responsáveis
  RESPONSIBLE_CREATED: 'responsible.created',
  RESPONSIBLE_UPDATED: 'responsible.updated',
  RESPONSIBLE_DELETED: 'responsible.deleted',
  // Usuários
  USER_ADDED: 'user.added',
  USER_UPDATED: 'user.updated',
  USER_ROLE_CHANGED: 'user.role_changed',
  USER_REMOVED: 'user.removed',
  // Tenant
  TENANT_UPDATED: 'tenant.updated',
  TENANT_SUSPENDED: 'tenant.suspended',
  TENANT_REACTIVATED: 'tenant.reactivated',
  TENANT_DELETED: 'tenant.deleted',
  TENANT_ENTERED_SCOPE: 'tenant.entered_scope',
  TENANT_LEFT_SCOPE: 'tenant.left_scope',
  // Sistema
  WEBHOOK_PING: 'webhook.ping',
} as const;
export type WebhookEventType = (typeof WebhookEventType)[keyof typeof WebhookEventType];
export const ALL_WEBHOOK_EVENT_TYPES = Object.values(WebhookEventType) as [
  WebhookEventType,
  ...WebhookEventType[],
];
export const WebhookEventTypeSchema = z.enum(ALL_WEBHOOK_EVENT_TYPES);

/** Grupos para a UI (checkbox por grupo) e para a doc. */
export const WebhookEventGroup = {
  LABELS: 'labels',
  PRINTING: 'printing',
  PRODUCTS: 'products',
  SUPPLIERS: 'suppliers',
  PRINTERS: 'printers',
  LABEL_TEMPLATES: 'label_templates',
  RESPONSIBLES: 'responsibles',
  USERS: 'users',
  TENANTS: 'tenants',
  SYSTEM: 'system',
} as const;
export type WebhookEventGroup = (typeof WebhookEventGroup)[keyof typeof WebhookEventGroup];
export const ALL_WEBHOOK_EVENT_GROUPS = Object.values(WebhookEventGroup) as [
  WebhookEventGroup,
  ...WebhookEventGroup[],
];

/** Formato de `data.object` de cada evento — a doc gera o schema a partir disto. */
export const WebhookObjectKind = {
  LABEL: 'label',
  PRODUCT: 'product',
  SUPPLIER: 'supplier',
  PRINTER: 'printer',
  LABEL_TEMPLATE: 'label_template',
  RESPONSIBLE: 'responsible',
  USER: 'user',
  TENANT: 'tenant',
  /** `{ id, deleted: true }` — o recurso deixou de existir para o parceiro. */
  TOMBSTONE: 'tombstone',
  PING: 'ping',
} as const;
export type WebhookObjectKind = (typeof WebhookObjectKind)[keyof typeof WebhookObjectKind];

export type WebhookEventCatalogEntry = {
  group: WebhookEventGroup;
  /** Scope de leitura que a integração precisa ter. `null` = sempre permitido (ping). */
  scope: ApiScope | null;
  object: WebhookObjectKind;
  /** Campos extras em `data`, além de `object` (doc). */
  extra?: readonly WebhookDataExtraField[];
  /** Descrição para a doc pública (inglês). */
  description: string;
};

/** Campos extras opcionais de `data`, por evento. */
export const WebhookDataExtraField = {
  /** `PublicLabelBaixaDto` — o movimento que gerou o evento. */
  MOVEMENT: 'movement',
  /** Horas até o vencimento (`label.expiring_soon`): 48 ou 24. */
  EXPIRES_IN_HOURS: 'expiresInHours',
  /** Papel anterior (`user.role_changed`). */
  PREVIOUS_ROLE: 'previousRole',
  /** Saldo vigente somado do produto (`product.stock_*`). */
  STOCK: 'stock',
  /** Motivo de falha (`label.print_failed`), texto curto não-contratual. */
  REASON: 'reason',
  /**
   * `{ kind: 'inbound_webhook' }` quando o fato foi CAUSADO por um webhook de
   * entrada (#803) — o parceiro reconhece o eco do que ele mesmo enviou e não
   * entra em laço (recebe `product.updated`, atualiza o dele, reenvia…).
   * Ausente quando a causa foi o app, o admin ou um processo do servidor.
   */
  CAUSE: 'cause',
} as const;
export type WebhookDataExtraField =
  (typeof WebhookDataExtraField)[keyof typeof WebhookDataExtraField];

const E = WebhookEventType;
const G = WebhookEventGroup;
const O = WebhookObjectKind;
const X = WebhookDataExtraField;

/**
 * Catálogo completo. `Record` exaustivo: tipo novo sem entrada não compila.
 *
 * Por quê do scope por evento: receber o webhook de um recurso é LER o recurso;
 * a integração sem `products:read` não pode ver `product.updated` por outro
 * caminho. Impressão é fato da etiqueta, então `labels:read`.
 */
export const WEBHOOK_EVENT_CATALOG: Record<WebhookEventType, WebhookEventCatalogEntry> = {
  [E.LABEL_PRINTED]: {
    group: G.LABELS, scope: ApiScope.LABELS_READ, object: O.LABEL,
    description: 'A label was printed for the first time (draft → printed).',
  },
  [E.LABEL_CONSUMED]: {
    group: G.LABELS, scope: ApiScope.LABELS_READ, object: O.LABEL, extra: [X.CAUSE],
    description: 'A label was closed as fully consumed.',
  },
  [E.LABEL_DISCARDED]: {
    group: G.LABELS, scope: ApiScope.LABELS_READ, object: O.LABEL, extra: [X.CAUSE],
    description: 'A label was closed as fully discarded (waste).',
  },
  [E.LABEL_EXPIRED]: {
    group: G.LABELS, scope: ApiScope.LABELS_READ, object: O.LABEL,
    description: 'A label was closed as expired (automatically, after its expiry time).',
  },
  [E.LABEL_MIXED]: {
    group: G.LABELS, scope: ApiScope.LABELS_READ, object: O.LABEL, extra: [X.CAUSE],
    description: 'A label was closed with more than one outcome (e.g. part consumed, part discarded). Read `baixas` for the composition.',
  },
  [E.LABEL_DELETED]: {
    group: G.LABELS, scope: ApiScope.LABELS_READ, object: O.TOMBSTONE,
    description: 'A printed label was deleted (moved to the trash). It no longer appears in GET /labels.',
  },
  [E.LABEL_RESTORED]: {
    group: G.LABELS, scope: ApiScope.LABELS_READ, object: O.LABEL,
    description: 'A deleted label was restored and is active again.',
  },
  [E.LABEL_UNDONE]: {
    group: G.LABELS, scope: ApiScope.LABELS_READ, object: O.LABEL, extra: [X.CAUSE],
    description: 'The closing of a label was undone; the label is active again.',
  },
  [E.LABEL_MOVEMENT_RECORDED]: {
    group: G.LABELS, scope: ApiScope.LABELS_READ, object: O.LABEL, extra: [X.MOVEMENT, X.CAUSE],
    description: 'A quantity movement (consume, discard, expire or undo) was recorded on a label, partial or not. `data.movement` is the entry; the label totals are already net.',
  },
  [E.LABEL_OVERRUN]: {
    group: G.LABELS, scope: ApiScope.LABELS_READ, object: O.LABEL,
    description: 'Movements on a label added up to more than the printed quantity (`overrunQuantity` grew). Auditable anomaly, usually concurrent offline writes.',
  },
  [E.LABEL_EXPIRING_SOON]: {
    group: G.LABELS, scope: ApiScope.LABELS_READ, object: O.LABEL, extra: [X.EXPIRES_IN_HOURS],
    description: 'An active label will expire within 48 h, and again within 24 h. `data.expiresInHours` says which.',
  },
  [E.LABEL_REPRINTED]: {
    group: G.PRINTING, scope: ApiScope.LABELS_READ, object: O.LABEL,
    description: 'An already printed label was printed again.',
  },
  [E.LABEL_PRINT_FAILED]: {
    group: G.PRINTING, scope: ApiScope.LABELS_READ, object: O.LABEL, extra: [X.REASON],
    // Auditoria #792 (correção, achado 2): falha na PRIMEIRA impressão deixa a
    // etiqueta em rascunho, que a API não expõe — não há objeto para mandar.
    description: 'A print attempt of an already printed label failed on the device (failures before the first successful print are not sent — drafts are not exposed). `data.reason` is informative only.',
  },
  [E.PRODUCT_CREATED]: {
    group: G.PRODUCTS, scope: ApiScope.PRODUCTS_READ, object: O.PRODUCT, extra: [X.CAUSE],
    description: 'A product was created.',
  },
  [E.PRODUCT_UPDATED]: {
    group: G.PRODUCTS, scope: ApiScope.PRODUCTS_READ, object: O.PRODUCT, extra: [X.CAUSE],
    description: 'A product changed (name, category, unit or validity rules). Stock levels have their own events.',
  },
  [E.PRODUCT_ACTIVATED]: {
    group: G.PRODUCTS, scope: ApiScope.PRODUCTS_READ, object: O.PRODUCT, extra: [X.CAUSE],
    description: 'An archived product was reactivated.',
  },
  [E.PRODUCT_DEACTIVATED]: {
    group: G.PRODUCTS, scope: ApiScope.PRODUCTS_READ, object: O.PRODUCT, extra: [X.CAUSE],
    description: 'A product was archived.',
  },
  [E.PRODUCT_DELETED]: {
    group: G.PRODUCTS, scope: ApiScope.PRODUCTS_READ, object: O.TOMBSTONE,
    description: 'A product was permanently deleted.',
  },
  [E.PRODUCT_STOCK_LOW]: {
    group: G.PRODUCTS, scope: ApiScope.PRODUCTS_READ, object: O.PRODUCT, extra: [X.STOCK],
    description: 'The active stock of a product fell below its minimum.',
  },
  [E.PRODUCT_STOCK_EMPTY]: {
    group: G.PRODUCTS, scope: ApiScope.PRODUCTS_READ, object: O.PRODUCT, extra: [X.STOCK],
    description: 'The active stock of a product with a minimum reached zero.',
  },
  [E.PRODUCT_STOCK_RESTORED]: {
    group: G.PRODUCTS, scope: ApiScope.PRODUCTS_READ, object: O.PRODUCT, extra: [X.STOCK],
    description: 'The active stock of a product is back at or above its minimum.',
  },
  [E.SUPPLIER_CREATED]: {
    group: G.SUPPLIERS, scope: ApiScope.SUPPLIERS_READ, object: O.SUPPLIER,
    description: 'A supplier was created.',
  },
  [E.SUPPLIER_UPDATED]: {
    group: G.SUPPLIERS, scope: ApiScope.SUPPLIERS_READ, object: O.SUPPLIER,
    description: 'A supplier changed.',
  },
  [E.SUPPLIER_ACTIVATED]: {
    group: G.SUPPLIERS, scope: ApiScope.SUPPLIERS_READ, object: O.SUPPLIER,
    description: 'An inactive supplier was reactivated.',
  },
  [E.SUPPLIER_DEACTIVATED]: {
    group: G.SUPPLIERS, scope: ApiScope.SUPPLIERS_READ, object: O.SUPPLIER,
    description: 'A supplier was deactivated.',
  },
  [E.PRINTER_CREATED]: {
    group: G.PRINTERS, scope: ApiScope.PRINTERS_READ, object: O.PRINTER,
    description: 'A printer was registered.',
  },
  [E.PRINTER_UPDATED]: {
    group: G.PRINTERS, scope: ApiScope.PRINTERS_READ, object: O.PRINTER,
    description: 'A printer changed (name, model, active).',
  },
  [E.PRINTER_DELETED]: {
    group: G.PRINTERS, scope: ApiScope.PRINTERS_READ, object: O.TOMBSTONE,
    description: 'A printer was removed.',
  },
  [E.LABEL_TEMPLATE_CREATED]: {
    group: G.LABEL_TEMPLATES, scope: ApiScope.TEMPLATES_READ, object: O.LABEL_TEMPLATE,
    description: 'A label template was created.',
  },
  [E.LABEL_TEMPLATE_UPDATED]: {
    group: G.LABEL_TEMPLATES, scope: ApiScope.TEMPLATES_READ, object: O.LABEL_TEMPLATE,
    description: 'A label template changed.',
  },
  [E.LABEL_TEMPLATE_DELETED]: {
    group: G.LABEL_TEMPLATES, scope: ApiScope.TEMPLATES_READ, object: O.TOMBSTONE,
    description: 'A label template was deleted.',
  },
  [E.RESPONSIBLE_CREATED]: {
    group: G.RESPONSIBLES, scope: ApiScope.RESPONSIBLES_READ, object: O.RESPONSIBLE,
    description: 'A person responsible for food handling was added.',
  },
  [E.RESPONSIBLE_UPDATED]: {
    group: G.RESPONSIBLES, scope: ApiScope.RESPONSIBLES_READ, object: O.RESPONSIBLE,
    description: 'A responsible person changed (active, default).',
  },
  [E.RESPONSIBLE_DELETED]: {
    group: G.RESPONSIBLES, scope: ApiScope.RESPONSIBLES_READ, object: O.TOMBSTONE,
    description: 'A responsible person was deleted.',
  },
  [E.USER_ADDED]: {
    group: G.USERS, scope: ApiScope.USERS_READ, object: O.USER,
    description: 'A user gained access to the company.',
  },
  [E.USER_UPDATED]: {
    group: G.USERS, scope: ApiScope.USERS_READ, object: O.USER,
    description: 'A user changed their display name.',
  },
  [E.USER_ROLE_CHANGED]: {
    group: G.USERS, scope: ApiScope.USERS_READ, object: O.USER, extra: [X.PREVIOUS_ROLE],
    description: 'The role of a user changed. `data.previousRole` holds the old one.',
  },
  [E.USER_REMOVED]: {
    group: G.USERS, scope: ApiScope.USERS_READ, object: O.TOMBSTONE,
    description: 'A user lost access to the company.',
  },
  [E.TENANT_UPDATED]: {
    group: G.TENANTS, scope: ApiScope.TENANTS_READ, object: O.TENANT,
    description: 'Company data changed (name, country, time zone).',
  },
  [E.TENANT_SUSPENDED]: {
    group: G.TENANTS, scope: ApiScope.TENANTS_READ, object: O.TENANT,
    description: 'The company was suspended; its users can no longer operate.',
  },
  [E.TENANT_REACTIVATED]: {
    group: G.TENANTS, scope: ApiScope.TENANTS_READ, object: O.TENANT,
    description: 'A suspended company was reactivated.',
  },
  [E.TENANT_DELETED]: {
    group: G.TENANTS, scope: ApiScope.TENANTS_READ, object: O.TOMBSTONE,
    description: 'The company was deleted. No further events will be sent for it.',
  },
  [E.TENANT_ENTERED_SCOPE]: {
    group: G.TENANTS, scope: ApiScope.TENANTS_READ, object: O.TENANT,
    description: 'A company became reachable by your integration (e.g. a new customer of your partner account).',
  },
  [E.TENANT_LEFT_SCOPE]: {
    group: G.TENANTS, scope: ApiScope.TENANTS_READ, object: O.TOMBSTONE,
    description: 'A company is no longer reachable by your integration. No further events will be sent for it.',
  },
  [E.WEBHOOK_PING]: {
    group: G.SYSTEM, scope: null, object: O.PING,
    description: 'Test event sent from the developer portal ("Send test event"). Always delivered, regardless of the selected events.',
  },
};

/**
 * Scope de leitura exigido por um tipo de evento (`null` = sempre permitido).
 *
 * Origem: #792 — 2026-10-02 12:53 +0200 (ver doc do módulo).
 *
 * Usado por: `isWebhookEventAllowed` (aqui) e `scripts/gen-portal-content.ts`
 * (coluna de scope na doc do portal).
 */
export function webhookScopeFor(type: WebhookEventType): ApiScope | null {
  return WEBHOOK_EVENT_CATALOG[type].scope;
}

/**
 * A integração com estes scopes pode receber este evento? Receber o webhook é
 * ler o recurso, empurrado — a regra é a mesma do REST.
 *
 * Origem: #792 — 2026-10-02 12:53 +0200 (ver doc do módulo).
 *
 * Usado por: `emitInner` (`webhooks/emit.ts`), `processDelivery`
 * (`webhooks/deliver.ts`) e `devPortal/webhooks.ts` (`assertEventsAllowed`, na
 * criação/edição da assinatura) — Functions. Avaliada na emissão E na entrega.
 */
export function isWebhookEventAllowed(
  type: WebhookEventType,
  scopes: readonly ApiScope[],
): boolean {
  const scope = webhookScopeFor(type);
  return scope === null || scopes.includes(scope);
}

/**
 * Eventos que o parceiro pode MARCAR numa assinatura. `webhook.ping` fica fora:
 * ele só sai pelo botão "Send test event" e é sempre entregue.
 */
export const SUBSCRIBABLE_WEBHOOK_EVENT_TYPES = ALL_WEBHOOK_EVENT_TYPES.filter(
  (type) => type !== WebhookEventType.WEBHOOK_PING,
);

// ---------------------------------------------------------------------------
// Envelope (o que o parceiro recebe)
// ---------------------------------------------------------------------------

/** `data.object` dos eventos de remoção. */
export const WebhookTombstoneSchema = z
  .object({ id: z.string(), deleted: z.literal(true) })
  .strict();
export type WebhookTombstone = z.infer<typeof WebhookTombstoneSchema>;

/**
 * Corpo JSON da entrega.
 *
 * Exemplo (`label.discarded`):
 *   {
 *     "id": "evt_3f1c…",                 // = webhook-id = Idempotency-Key
 *     "type": "label.discarded",
 *     "apiVersion": "v1",
 *     "occurredAtMillis": 1759400000000, // relógio do servidor
 *     "tenantId": "tenant-1",            // null só no webhook.ping
 *     "data": { "object": { …PublicLabelDto… } }
 *   }
 *
 * `data.object` é o MESMO DTO de `GET /v1/tenants/{id}/<recurso>` no instante
 * da emissão — não o estado de agora. Entrega é at-least-once e sem ordem
 * garantida: o parceiro usa `updatedAtMillis`/`statusChangedAtMillis` para
 * descartar o que for mais velho do que já tem.
 *
 * Origem: #792 — 2026-10-02 12:53 +0200 (ver doc do módulo).
 *
 * Usado por: `emitInner` (`webhooks/emit.ts`) monta e serializa; o parceiro
 * recebe e verifica com qualquer lib Standard Webhooks;
 * `scripts/gen-portal-content.ts` documenta o formato.
 */
export const WebhookEnvelopeSchema = z
  .object({
    id: z.string().min(1).max(80),
    type: WebhookEventTypeSchema,
    apiVersion: z.literal(WEBHOOK_API_VERSION),
    occurredAtMillis: z.number().int(),
    tenantId: z.string().nullable(),
    data: z.object({ object: z.unknown() }).catchall(z.unknown()),
  })
  .strict();
export type WebhookEnvelope = z.infer<typeof WebhookEnvelopeSchema>;

// ---------------------------------------------------------------------------
// Documentos Firestore (server-only; rules deny-all para cliente)
// ---------------------------------------------------------------------------

export const WebhookSubscriptionStatus = {
  ACTIVE: 'ACTIVE',
  /** Pausada pelo parceiro no portal, ou pela revogação da integração. */
  PAUSED: 'PAUSED',
  /** Desligada pelo servidor após entregas esgotadas seguidas. Reativar no portal. */
  DISABLED_FAILING: 'DISABLED_FAILING',
} as const;
export type WebhookSubscriptionStatus =
  (typeof WebhookSubscriptionStatus)[keyof typeof WebhookSubscriptionStatus];
export const ALL_WEBHOOK_SUBSCRIPTION_STATUSES = Object.values(WebhookSubscriptionStatus) as [
  WebhookSubscriptionStatus,
  ...WebhookSubscriptionStatus[],
];
export const WebhookSubscriptionStatusSchema = z.enum(ALL_WEBHOOK_SUBSCRIPTION_STATUSES);

/**
 * `outbound_webhook_subscriptions/{subId}` — gravado só pela callable
 * `devPortalWebhooks` (e pelo servidor, ao desligar/pausar). Timestamps em
 * millis do servidor (`Date.now()` na Function), não `serverTimestamp`, para o
 * parse ser direto.
 *
 * O segredo de assinatura NÃO está aqui: é `HMAC(WEBHOOK_SIGNING_MASTER,
 * subId:secretVersion)`, recalculado na entrega (`webhooks/signing.ts`).
 *
 * Origem: #792 — 2026-10-02 12:53 +0200 (ver doc do módulo).
 *
 * Usado por: `parseSubscription` (`webhooks/store.ts`, lido na emissão e na
 * entrega), `devPortal/webhooks.ts` (grava e expõe via
 * `PortalWebhookSubscriptionSchema`) e `admin/apiKeyWebhooks.ts` (pausa as
 * assinaturas ao revogar a integração, sem parse). Rules: deny-all para
 * cliente (`firebase/firestore.rules`, `outbound_webhooks.test.ts`).
 */
export const WebhookSubscriptionSchema = z.object({
  id: z.string(),
  /** `api_keys/{keyId}` — a integração dona; dela vêm alcance e scopes. */
  keyId: z.string().min(1),
  url: z.string().min(1).max(WEBHOOK_URL_MAX_LENGTH),
  description: z.string().max(WEBHOOK_DESCRIPTION_MAX_LENGTH),
  events: z.array(WebhookEventTypeSchema).min(1),
  status: WebhookSubscriptionStatusSchema,
  secretVersion: z.number().int().min(1),
  /** Até quando a versão `secretVersion - 1` também assina (rotação). */
  previousSecretValidUntilMillis: z.number().int().nullable(),
  consecutiveExhausted: z.number().int().min(0),
  disabledReason: z.string().max(200).nullable(),
  lastDeliveryAtMillis: z.number().int().nullable(),
  lastSuccessAtMillis: z.number().int().nullable(),
  createdAtMillis: z.number().int(),
  createdByEmail: z.string(),
  updatedAtMillis: z.number().int(),
  updatedByEmail: z.string(),
});
export type WebhookSubscription = z.infer<typeof WebhookSubscriptionSchema>;

export const WebhookDeliveryStatus = {
  /** Enfileirada, ainda sem tentativa concluída. */
  PENDING: 'PENDING',
  SUCCEEDED: 'SUCCEEDED',
  /** Última tentativa falhou; o Cloud Tasks vai tentar de novo. */
  RETRYING: 'RETRYING',
  /** Tentativas esgotadas. Só sai daqui por reenvio manual. */
  EXHAUSTED: 'EXHAUSTED',
  /**
   * Não enviada: na hora da entrega a assinatura não estava ativa, a integração
   * foi revogada, perdeu o scope, ou o tenant saiu do alcance.
   */
  SKIPPED: 'SKIPPED',
} as const;
export type WebhookDeliveryStatus =
  (typeof WebhookDeliveryStatus)[keyof typeof WebhookDeliveryStatus];
export const ALL_WEBHOOK_DELIVERY_STATUSES = Object.values(WebhookDeliveryStatus) as [
  WebhookDeliveryStatus,
  ...WebhookDeliveryStatus[],
];
export const WebhookDeliveryStatusSchema = z.enum(ALL_WEBHOOK_DELIVERY_STATUSES);

/**
 * `outbound_webhook_events/{evtId}` — o evento emitido, guardado uma vez e
 * referenciado pelas N entregas. `payload` é o envelope já serializado
 * (string), para a assinatura ser sobre os MESMOS bytes em toda tentativa.
 *
 * Origem: #792 — 2026-10-02 12:53 +0200 (ver doc do módulo).
 *
 * Usado por: `webhooks/emit.ts` (grava), `webhooks/deliver.ts` (lê a cada
 * tentativa) e `devPortal/webhooks.ts` (`getDelivery` devolve o `payload`).
 * TTL por `expireAt` (30 dias, `WEBHOOK_RETENTION_MS`) — override de campo em
 * `firebase/firestore.indexes.json`.
 */
export const WebhookEventDocSchema = z.object({
  id: z.string(),
  type: WebhookEventTypeSchema,
  tenantId: z.string().nullable(),
  payload: z.string(),
  occurredAtMillis: z.number().int(),
  expireAt: z.unknown(),
});
export type WebhookEventDoc = z.infer<typeof WebhookEventDocSchema>;

/**
 * Uma tentativa de entrega, para o log de auditoria do portal (#792).
 * `statusCode` null = não houve resposta HTTP (timeout, DNS, rede, bloqueio);
 * aí o motivo está em `error`. `redelivery` = número do reenvio manual (0 =
 * cadeia original).
 */
export const WebhookDeliveryAttemptSchema = z.object({
  atMillis: z.number().int(),
  statusCode: z.number().int().nullable(),
  error: z.string().max(300).nullable(),
  durationMs: z.number().int().min(0),
  redelivery: z.number().int().min(0),
});
export type WebhookDeliveryAttempt = z.infer<typeof WebhookDeliveryAttemptSchema>;

/**
 * `outbound_webhook_deliveries/{evtId}__{subId}` — uma por (evento, assinatura).
 *
 * Origem: #792 — 2026-10-02 12:53 +0200 (ver doc do módulo).
 *
 * Usado por: `webhooks/emit.ts` (cria `PENDING`), `webhooks/deliver.ts` (toda
 * tentativa), `webhooks/sweep.ts` (re-enfileira/fecha), `devPortal/webhooks.ts`
 * (lista, detalhe, reenvio) e o portal via `PortalWebhookDeliverySchema`.
 * Índices (status, createdAtMillis), (status, lastAttemptAtMillis) — este do
 * fechamento de `RETRYING` silencioso, #795 — e (subscriptionId/keyId,
 * createdAtMillis) em `firebase/firestore.indexes.json`. `history` ausente = entrega anterior ao
 * log de auditoria (2026-10-02 15:40).
 */
export const WebhookDeliverySchema = z.object({
  id: z.string(),
  eventId: z.string(),
  subscriptionId: z.string(),
  keyId: z.string(),
  tenantId: z.string().nullable(),
  type: WebhookEventTypeSchema,
  status: WebhookDeliveryStatusSchema,
  attempts: z.number().int().min(0),
  lastStatusCode: z.number().int().nullable(),
  lastError: z.string().max(300).nullable(),
  responseExcerpt: z.string().max(WEBHOOK_RESPONSE_EXCERPT_MAX).nullable(),
  lastAttemptAtMillis: z.number().int().nullable(),
  createdAtMillis: z.number().int(),
  /** Quantas vezes foi reenviada à mão pelo portal. */
  redeliveries: z.number().int().min(0),
  /**
   * Últimas `WEBHOOK_DELIVERY_HISTORY_MAX` tentativas, da mais antiga para a
   * mais recente. Ausente nas entregas gravadas antes do log de auditoria.
   */
  history: z.array(WebhookDeliveryAttemptSchema).max(WEBHOOK_DELIVERY_HISTORY_MAX).optional(),
  /**
   * Rodadas do sweep que reencontraram a entrega ainda `PENDING` (#795). Só o
   * sweep escreve; o reenvio manual zera. Ausente = nunca reencontrada.
   */
  pendingRequeues: z.number().int().min(0).optional(),
  expireAt: z.unknown(),
});
export type WebhookDelivery = z.infer<typeof WebhookDeliverySchema>;

/**
 * Id determinístico da entrega: `<evtId>__<subId>`, um por (evento, assinatura).
 *
 * Origem: #792 — 2026-10-02 12:53 +0200 (ver doc do módulo).
 *
 * Usado por: `emitInner` (`webhooks/emit.ts`) e `devPortal/webhooks.ts`
 * (`sendTest` devolve o id ao portal). O id de tarefa do Cloud Tasks deriva
 * dele (`deliveryTaskId`, `webhooks/store.ts`) — mudar o formato quebra o
 * dedupe de reenvio.
 */
export function webhookDeliveryId(eventId: string, subscriptionId: string): string {
  return `${eventId}__${subscriptionId}`;
}

// ---------------------------------------------------------------------------
// URL de destino — validação SINTÁTICA (pura)
// ---------------------------------------------------------------------------

export const WebhookUrlError = {
  INVALID: 'invalid_url',
  TOO_LONG: 'url_too_long',
  NOT_HTTPS: 'https_required',
  CREDENTIALS: 'credentials_not_allowed',
  PORT: 'port_not_allowed',
  IP_LITERAL: 'ip_address_not_allowed',
  PRIVATE_HOST: 'private_host_not_allowed',
} as const;
export type WebhookUrlError = (typeof WebhookUrlError)[keyof typeof WebhookUrlError];

const IPV4_LITERAL = /^\d{1,3}(\.\d{1,3}){3}$/;
/** Sufixos que nunca resolvem para a internet pública. */
const PRIVATE_HOST_SUFFIXES = ['.local', '.localhost', '.internal', '.lan', '.home', '.corp'];
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);

/**
 * Validação sintática da URL de destino — portal (feedback imediato) e
 * servidor (na gravação). NÃO substitui a checagem de rede da entrega
 * (`webhooks/safeFetch.ts`), que resolve o DNS e bloqueia IP privado: um
 * hostname público pode apontar para 10.0.0.1, e só a resolução descobre.
 *
 * `allowInsecureLocalhost` só é `true` no emulador de Functions — é o que
 * permite o teste local contra um receptor em `http://localhost` (#792).
 *
 * Origem: #792 — 2026-10-02 12:53 +0200 (ver doc do módulo).
 *
 * Contrato: `{ ok: true, url }` com a URL normalizada, ou `{ ok: false, error }`
 * com um `WebhookUrlError` — o portal traduz o código em texto
 * (`describeWebhookError`). Puro.
 *
 * Usado por: `postWebhook` (`webhooks/safeFetch.ts`), `assertUrl`
 * (`devPortal/webhooks.ts`) e `WebhookEditorPage` (`apps/developers`, feedback
 * ao digitar).
 *
 * Falhas conhecidas:
 *  - 2026-10-02 (#792, auditoria, achado 4): hostname com ponto final
 *    (`localhost.`) escapava do filtro de sufixo — hoje é normalizado antes.
 */
export function validateWebhookUrl(
  raw: string,
  options: { allowInsecureLocalhost?: boolean } = {},
): { ok: true; url: string } | { ok: false; error: WebhookUrlError } {
  const value = raw.trim();
  if (value.length > WEBHOOK_URL_MAX_LENGTH) return { ok: false, error: WebhookUrlError.TOO_LONG };
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return { ok: false, error: WebhookUrlError.INVALID };
  }
  // Ponto final (`localhost.`, `metadata.google.internal.`) é o mesmo host para
  // o DNS e escapava das checagens de sufixo (auditoria #792, achado 4).
  const host = parsed.hostname.toLowerCase().replace(/\.+$/, '');
  if (options.allowInsecureLocalhost === true && LOCAL_HOSTS.has(host)) {
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return { ok: false, error: WebhookUrlError.INVALID };
    }
    return { ok: true, url: parsed.toString() };
  }
  if (parsed.protocol !== 'https:') return { ok: false, error: WebhookUrlError.NOT_HTTPS };
  if (parsed.username !== '' || parsed.password !== '') {
    return { ok: false, error: WebhookUrlError.CREDENTIALS };
  }
  // Porta só a default. Porta arbitrária é o vetor clássico de varredura de
  // serviço interno por SSRF, e nenhum endpoint de webhook público precisa dela.
  if (parsed.port !== '' && parsed.port !== '443') return { ok: false, error: WebhookUrlError.PORT };
  if (IPV4_LITERAL.test(host) || host.startsWith('[') || host.includes(':')) {
    return { ok: false, error: WebhookUrlError.IP_LITERAL };
  }
  if (
    LOCAL_HOSTS.has(host) ||
    !host.includes('.') ||
    PRIVATE_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix))
  ) {
    return { ok: false, error: WebhookUrlError.PRIVATE_HOST };
  }
  // Fragmento não vai na requisição; descartar evita guardar algo que não é usado.
  parsed.hash = '';
  return { ok: true, url: parsed.toString() };
}

// ---------------------------------------------------------------------------
// Callable `devPortalWebhooks` (portal → Functions)
// ---------------------------------------------------------------------------

/**
 * Idempotency-Key das ações do portal: o cliente gera um id por INTENÇÃO
 * (clique em "Criar", "Rotacionar", "Reenviar", "Enviar teste") e o reenvia em
 * retry. O servidor guarda a resposta por 24 h — repetir devolve o mesmo
 * resultado (o mesmo segredo, a mesma assinatura), nunca uma segunda.
 *
 * Origem: #792 — 2026-10-02 13:10 +0200 (callable `devPortalWebhooks`).
 *
 * Usado por: `DevPortalWebhooksInputSchema` (aqui); gerada por
 * `newIdempotencyKey` em `apps/developers/src/lib/webhooksApi.ts`; guardada em
 * `outbound_webhook_idempotency` por `withIdempotency` (`devPortal/webhooks.ts`).
 */
export const WebhookIdempotencyKeySchema = z
  .string()
  .min(8)
  .max(128)
  .regex(/^[A-Za-z0-9_-]+$/, 'invalid_idempotency_key');

const KeyIdSchema = z.string().min(1).max(64);
const SubscriptionIdSchema = z.string().min(1).max(64);
const SubscribableEventsSchema = z
  .array(WebhookEventTypeSchema)
  .min(1, 'events_required')
  .max(ALL_WEBHOOK_EVENT_TYPES.length)
  .refine((events) => new Set(events).size === events.length, 'duplicate_event')
  .refine((events) => !events.includes(WebhookEventType.WEBHOOK_PING), 'ping_not_subscribable');
const UrlInputSchema = z.string().trim().min(1).max(WEBHOOK_URL_MAX_LENGTH);
const DescriptionSchema = z.string().trim().max(WEBHOOK_DESCRIPTION_MAX_LENGTH);

export const DevPortalWebhooksAction = {
  CONTEXT: 'context',
  CREATE: 'create',
  UPDATE: 'update',
  SET_STATUS: 'setStatus',
  DELETE: 'delete',
  ROTATE_SECRET: 'rotateSecret',
  SEND_TEST: 'sendTest',
  LIST_DELIVERIES: 'listDeliveries',
  GET_DELIVERY: 'getDelivery',
  LIST_EVENTS: 'listEvents',
  REDELIVER: 'redeliver',
} as const;
export type DevPortalWebhooksAction =
  (typeof DevPortalWebhooksAction)[keyof typeof DevPortalWebhooksAction];

/**
 * Input da callable `devPortalWebhooks`, discriminado por `action`
 * (`DevPortalWebhooksAction`). Toda ação que muta leva `idempotencyKey`.
 *
 * Origem: #792 — 2026-10-02 13:10 +0200 (commit `05bc43383`); `listEvents` em
 * 2026-10-02 15:40 +0200.
 *
 * Usado por: `handleDevPortalWebhooks` (`devPortal/webhooks.ts`, Functions —
 * `parse` na borda) e `apps/developers/src/lib/webhooksApi.ts` (tipo do input,
 * `DevPortalWebhooksInput` = `z.input`, para `description` ser opcional no
 * cliente).
 */
export const DevPortalWebhooksInputSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal(DevPortalWebhooksAction.CONTEXT) }),
  z.object({
    action: z.literal(DevPortalWebhooksAction.CREATE),
    keyId: KeyIdSchema,
    url: UrlInputSchema,
    description: DescriptionSchema.default(''),
    events: SubscribableEventsSchema,
    idempotencyKey: WebhookIdempotencyKeySchema,
  }),
  z.object({
    action: z.literal(DevPortalWebhooksAction.UPDATE),
    keyId: KeyIdSchema,
    subscriptionId: SubscriptionIdSchema,
    url: UrlInputSchema.optional(),
    description: DescriptionSchema.optional(),
    events: SubscribableEventsSchema.optional(),
  }),
  z.object({
    action: z.literal(DevPortalWebhooksAction.SET_STATUS),
    keyId: KeyIdSchema,
    subscriptionId: SubscriptionIdSchema,
    /** O parceiro só alterna entre ativo e pausado; `DISABLED_FAILING` é do servidor. */
    status: z.enum([WebhookSubscriptionStatus.ACTIVE, WebhookSubscriptionStatus.PAUSED]),
  }),
  z.object({
    action: z.literal(DevPortalWebhooksAction.DELETE),
    keyId: KeyIdSchema,
    subscriptionId: SubscriptionIdSchema,
  }),
  z.object({
    action: z.literal(DevPortalWebhooksAction.ROTATE_SECRET),
    keyId: KeyIdSchema,
    subscriptionId: SubscriptionIdSchema,
    idempotencyKey: WebhookIdempotencyKeySchema,
  }),
  z.object({
    action: z.literal(DevPortalWebhooksAction.SEND_TEST),
    keyId: KeyIdSchema,
    subscriptionId: SubscriptionIdSchema,
    idempotencyKey: WebhookIdempotencyKeySchema,
  }),
  z.object({
    action: z.literal(DevPortalWebhooksAction.LIST_DELIVERIES),
    keyId: KeyIdSchema,
    subscriptionId: SubscriptionIdSchema,
    /** Cursor opaco devolvido pela página anterior (vale para os mesmos filtros). */
    pageToken: z.string().max(200).optional(),
    /** Filtros do log de auditoria (#792). Combináveis. */
    status: WebhookDeliveryStatusSchema.optional(),
    type: WebhookEventTypeSchema.optional(),
    /** Busca exata por id do evento (= `Idempotency-Key`); ignora os outros filtros. */
    eventId: z.string().trim().regex(/^evt_[0-9a-f]{32}$/, 'invalid_event_id').optional(),
  }),
  z.object({
    action: z.literal(DevPortalWebhooksAction.LIST_EVENTS),
    keyId: KeyIdSchema,
    /** Cursor opaco da página anterior (vale para os mesmos filtros). */
    pageToken: z.string().max(200).optional(),
    /** Filtros da tela "Event log" (#792). Todos opcionais e combináveis. */
    subscriptionId: SubscriptionIdSchema.optional(),
    status: WebhookDeliveryStatusSchema.optional(),
    type: WebhookEventTypeSchema.optional(),
    tenantId: z.string().trim().min(1).max(128).optional(),
    /** Janela de criação da entrega (epoch ms, inclusiva). */
    fromMillis: z.number().int().min(0).optional(),
    toMillis: z.number().int().min(0).optional(),
    /** Busca exata por id do evento (= Idempotency-Key); ignora os outros filtros. */
    eventId: z.string().trim().regex(/^evt_[0-9a-f]{32}$/, 'invalid_event_id').optional(),
  }),
  z.object({
    action: z.literal(DevPortalWebhooksAction.GET_DELIVERY),
    keyId: KeyIdSchema,
    subscriptionId: SubscriptionIdSchema,
    deliveryId: z.string().min(1).max(200),
  }),
  z.object({
    action: z.literal(DevPortalWebhooksAction.REDELIVER),
    keyId: KeyIdSchema,
    subscriptionId: SubscriptionIdSchema,
    deliveryId: z.string().min(1).max(200),
    idempotencyKey: WebhookIdempotencyKeySchema,
  }),
]);
export type DevPortalWebhooksInput = z.input<typeof DevPortalWebhooksInputSchema>;
export type DevPortalWebhooksParsedInput = z.infer<typeof DevPortalWebhooksInputSchema>;

/**
 * Assinatura como o portal a vê (sem `secretVersion`, contadores e autores).
 *
 * Origem: #792 — 2026-10-02 13:10 +0200.
 *
 * Usado por: `toPortalSubscription` (`devPortal/webhooks.ts`) e as telas
 * `Webhook*Page.tsx` de `apps/developers`.
 */
export const PortalWebhookSubscriptionSchema = WebhookSubscriptionSchema.pick({
  id: true,
  keyId: true,
  url: true,
  description: true,
  events: true,
  status: true,
  disabledReason: true,
  lastDeliveryAtMillis: true,
  lastSuccessAtMillis: true,
  createdAtMillis: true,
  updatedAtMillis: true,
  previousSecretValidUntilMillis: true,
});
export type PortalWebhookSubscription = z.infer<typeof PortalWebhookSubscriptionSchema>;

/**
 * Integração como o portal a vê: o suficiente para montar a tela (nome, scopes,
 * alcance e as assinaturas dela).
 *
 * Origem: #792 — 2026-10-02 13:10 +0200.
 *
 * Usado por: ação `context` de `devPortal/webhooks.ts`;
 * `useWebhooksContext`/`describeReach` em `apps/developers`.
 */
export const PortalWebhookIntegrationSchema = z.object({
  keyId: z.string(),
  name: z.string(),
  clientId: z.string(),
  scopes: z.array(ApiScopeSchema),
  tenantScope: ApiTenantScopeSchema,
  /** Nome do parceiro quando `tenantScope === PARTNER` (para o texto de alcance). */
  scopePartnerName: z.string().nullable(),
  demoTenantCount: z.number().int().min(0),
  subscriptions: z.array(PortalWebhookSubscriptionSchema),
});
export type PortalWebhookIntegration = z.infer<typeof PortalWebhookIntegrationSchema>;

/**
 * Entrega como o portal a vê (sem `keyId` nem `expireAt`).
 *
 * Origem: #792 — 2026-10-02 13:10 +0200.
 *
 * Usado por: `toPortalDelivery` (`devPortal/webhooks.ts`), `WebhookDetailPage`
 * e `WebhookEventsPage` (`apps/developers`).
 */
export const PortalWebhookDeliverySchema = WebhookDeliverySchema.omit({
  keyId: true,
  expireAt: true,
});
export type PortalWebhookDelivery = z.infer<typeof PortalWebhookDeliverySchema>;

/** Respostas por ação. */
export type DevPortalWebhooksContextResult = {
  integrations: PortalWebhookIntegration[];
  maxSubscriptionsPerKey: number;
};
export type DevPortalWebhooksSecretResult = {
  subscription: PortalWebhookSubscription;
  /** `whsec_…` — exibido uma vez; não é recuperável depois. */
  secret: string;
};
export type DevPortalWebhooksSubscriptionResult = { subscription: PortalWebhookSubscription };
export type DevPortalWebhooksDeleteResult = { deleted: true };
export type DevPortalWebhooksSendResult = { eventId: string; deliveryId: string };
export type DevPortalWebhooksDeliveriesResult = {
  deliveries: PortalWebhookDelivery[];
  nextPageToken: string | null;
  /**
   * Total por status nos últimos 30 dias (retenção), da assinatura inteira —
   * independe dos filtros. Só na primeira página (`pageToken` ausente).
   */
  counts: Record<WebhookDeliveryStatus, number> | null;
};

/**
 * Página da tela "Event log" (#792): entregas de TODOS os endpoints da
 * integração, mais recentes primeiro.
 *
 * `scanned` = docs lidos para montar a página. Com filtros que o índice não
 * cobre (tenant, tipo, endpoint), o servidor varre por ordem de data até
 * juntar a página ou bater no teto de leituras — por isso uma página pode vir
 * com menos itens e ainda ter `nextPageToken`.
 */
export type DevPortalWebhooksEventsResult = {
  deliveries: PortalWebhookDelivery[];
  nextPageToken: string | null;
  scanned: number;
  /** Total por status da integração (30 dias), só na 1ª página. */
  counts: Record<WebhookDeliveryStatus, number> | null;
};

/**
 * Detalhe de uma entrega para auditoria: a entrega + o corpo EXATO que foi
 * assinado e enviado (`payload`, string) e os headers que o acompanham (sem a
 * assinatura, que muda a cada tentativa). `payload` null = o evento já saiu da
 * retenção.
 */
export type DevPortalWebhooksDeliveryDetailResult = {
  delivery: PortalWebhookDelivery;
  payload: string | null;
  headers: Record<string, string>;
};
